import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ErrorNotice } from '@/components/ui/misc';

/**
 * The last line of defence.
 *
 * A render-time exception anywhere below here is caught and turned into a recoverable
 * screen rather than a blank page. The technical detail goes to the console for a
 * developer; the user sees a message and a way out. Resetting the key on "Try again"
 * remounts the subtree, so a transient failure does not require a full reload.
 */
interface State {
  error: Error | undefined;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: undefined };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Unhandled error in the application UI', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <div className="w-full max-w-md space-y-3">
            <ErrorNotice
              title="Something went wrong"
              message="An unexpected error interrupted this page. Reloading usually clears it."
            />
            <div className="flex gap-2">
              <Button onClick={() => this.setState({ error: undefined })}>Try again</Button>
              <Button variant="outline" onClick={() => window.location.assign('/')}>
                Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
